import React, { useState } from "react";
import { Stack, Text } from "@origin-digital/ods-core";
import { IconCancel, IconChevronLeft } from "@origin-digital/ods-icons";
import styled from "styled-components";
import { FullSelect } from "./PropertySelect";
import { NavTitle } from "./NavTitle";
import {
  getSelectProps,
  hasSelectedPremise,
  isSinglePremise,
  isEmptyPremise,
} from "./utils";
import { ActionBarProps } from "./ActionBar";
import { PropertyPopper } from "./PropertyPopper/PropertyPopper";
import { Box } from "./Box";

export const IconButton = styled.button`
  appearance: none;
  align-items: center;
  background-color: transparent;
  border: 0;
  box-shadow: none;
  box-sizing: border-box;
  cursor: pointer;
  display: inline-flex;
  justify-content: center;
  line-height: 24px;
  padding: 12px;
  user-select: none;
  vertical-align: middle;
  width: auto;
  outline: none;
`;

const CloseText = styled(Text)`
  padding-left: 4px;
`;

export interface ActionBarMobileProps {
  mobilePropertySelector: ActionBarProps["mobilePropertySelector"];
  mobileNavButton: ActionBarProps["mobileNavButton"];
  premises: ActionBarProps["premises"];
  selectedPremiseId: ActionBarProps["selectedPremiseId"];
  headingComponent: ActionBarProps["headingComponent"];
  handleOnSelectPremise: any;
  onClickMobileNavButton: ActionBarProps["onClickMobileNavButton"];
  title?: string;
  desktopVariant?: ActionBarProps["desktopVariant"];
  isDesktop?: boolean;
}

export function getMobileNavButton(
  mobileNavButton: ActionBarProps["mobileNavButton"],
  onClickMobileNavButton: ActionBarProps["onClickMobileNavButton"],
  isDesktop: boolean = false,
  desktopVariant?: ActionBarProps["desktopVariant"]
) {
  if (mobileNavButton === "none") {
    return null;
  } else if (mobileNavButton === "close") {
    return (
      <IconButton
        data-id={`button-close${isDesktop ? "-desktop" : ""}`}
        onClick={onClickMobileNavButton}
      >
        <IconCancel tone="critical" />
        {desktopVariant === "slim" ? (
          <CloseText tone="critical" weight="medium">
            Close
          </CloseText>
        ) : null}
      </IconButton>
    );
  } else if (mobileNavButton === "back") {
    return (
      <IconButton
        data-id={`button-back${isDesktop ? "-desktop" : ""}`}
        onClick={onClickMobileNavButton}
      >
        <IconChevronLeft tone="critical" />
        {desktopVariant === "slim" ? (
          <Text tone="critical" weight="medium">
            Back
          </Text>
        ) : null}
      </IconButton>
    );
  }
  return null;
}

export const FullContainer = styled(Box)`
  height: 48px;
  border-top: 1px solid ${(p) => p.theme.colors.grey200};
`;

export const ActionBarMobile = ({
  mobileNavButton,
  mobilePropertySelector,
  handleOnSelectPremise,
  premises,
  selectedPremiseId,
  headingComponent,
  onClickMobileNavButton,
  title,
}: ActionBarMobileProps) => {
  const [mobileRef, setMobileRef] = useState<HTMLElement | null>(null);

  return (
    <div ref={setMobileRef}>
      <Stack space="none" data-id="mobile-container">
        <NavTitle
          mobilePropertySelector={mobilePropertySelector}
          premises={premises}
          selectedPremiseId={selectedPremiseId}
          handleOnSelectPremise={handleOnSelectPremise}
          mobileNavButton={mobileNavButton}
          headingComponent={headingComponent}
          onClickMobileNavButton={onClickMobileNavButton}
          title={title}
        />
        {mobilePropertySelector === "full" &&
        hasSelectedPremise(premises, selectedPremiseId) &&
        !isSinglePremise(premises) &&
        !isEmptyPremise(premises) ? (
          <FullContainer
            backgroundColor="white"
            display="flex"
            paddingLeft="medium"
            paddingRight="small"
            alignItems="center"
            justifyContent="center"
          >
            <FullSelect
              data-id="actionbar-address-select-full-mobile"
              {...getSelectProps({
                handleOnSelectPremise,
                premises,
                selectedPremiseId,
              })}
            />
            <PropertyPopper id="mobile-popper" anchorEl={mobileRef} />
          </FullContainer>
        ) : null}
      </Stack>
    </div>
  );
};
